import React, { useState, useEffect } from "react";
import { useOidcAccessToken } from "@axa-fr/react-oidc";
import {
  Card,
  CardBody,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
  Modal,
  ModalBody,
  ModalHeader,
  Button,
} from "reactstrap";
import BreadcrumbItem from "../../Components/common/Breadcrumb";
import {
  buildQuerySort,
  buildQuerySearch,
  modalSettingsDefault,
  getUniqueSelectors,
  orderBykey,
  callErrorToast,
  getClassForMultiActionElement,
} from "../../utilities";
import {
  allSelectedCollectionCanBeDeleted,
  allSelectedCollectionsCanBeDeleted,
  canAllCollectionsBeDeleted,
  columns,
} from "./utilities";
import TableAdvanced from "../../Components/common/tableAdvanced/TableAdvanced";
import useFetch from "../../customHooks/useFetch";
import { ContractsService } from "../../services/contract/contracts";
import CreateEdit from "./CreateEdit";
import Delete from "./Delete.jsx";
import Book from "./Book";
import Details from "./Details";
import { useAppContext } from "../../AppProvider";
import { collectionStatusList } from "../../constants";
import {
  getCarrierOptionStyle,
  getShipmentStatusOptionStyle,
} from "../../Components/common/AsyncSelect/utilities";
import { getAllCarriers } from "../../utilities/asyncSelectCallAPI";
import moment from "moment";

const Collections = () => {
  const { accessToken, accessTokenPayload } = useOidcAccessToken();

  const [pagination, setPagination] = useState({
    page: 0,
    size: 10,
  });
  const [sort, setSort] = useState([
    {
      orderBy: "desc",
      column: "date",
    },
  ]);
  const [freeSearch, setFreeSearch] = useState("");
  const [queryParams, setQueryParams] = useState([]);
  const [querySearch, setQuerySearch] = useState();
  const [modalSettings, setModalSettings] = useState({ modalSettingsDefault });
  const [statusOfTableRows, setStatusOfTableRows] = useState([]);

  const { dictionary } = useAppContext();

  const modalToggle = () => {
    refreshData();
    setModalSettings({ modalSettingsDefault, open: !modalSettings.open });
  };

  const handleSorting = (sortingOpt) => {
    setSort((sort) => {
      const sortIndex = sort.findIndex((x) => x.column === sortingOpt.column);
      let newSort = [...sort];
      if (sortIndex !== -1) {
        newSort[sortIndex] = sortingOpt;
      } else {
        newSort = [];
        newSort.push(sortingOpt);
      }

      return newSort;
    });
  };

  const handlePagination = (paginationOpt) => {
    setPagination((pagination) => {
      return { ...pagination, ...paginationOpt };
    });
  };

  const onSearch = (selector, value, preciseSearch = false) => {
    setQueryParams((queryParams) => {
      return getUniqueSelectors(selector, value, preciseSearch, queryParams);
    });
    handlePagination({ page: 0 });
  };

  const { data, loading, error, refreshData } = useFetch(
    `/shipment-service/collections?page=${pagination.page}&size=${
      pagination.size
    }${sort.length > 0 ? buildQuerySort(sort) : ""}${
      queryParams.length > 0 ? `&search=${buildQuerySearch(queryParams)}` : ""
    }${freeSearch ? `&freeSearch=${freeSearch}` : ""}`
  );

  const onChangeStatusRow = (changeStatusForRows) => {
    setStatusOfTableRows((previousStatusOfTableRows) => {
      changeStatusForRows.forEach((changedRow) => {
        const existingRow = previousStatusOfTableRows.find(
          (row) => row.id === changedRow.id
        );
        if (existingRow) {
          existingRow.value = changedRow.value;
        } else {
          previousStatusOfTableRows.push(changedRow);
        }
      });
      return [...previousStatusOfTableRows];
    });
  };

  const onChangeStatusAllRows = (val) => {
    const newSelectedRow = data.content.map((x) => {
      return { id: x.id, value: val };
    });
    setStatusOfTableRows(newSelectedRow);
  };

  useEffect(() => {
    setQuerySearch(buildQuerySearch(queryParams));
  }, [queryParams, querySearch, pagination, sort]);

  const allColumns = orderBykey(
    [
      {
        position: 1,
        Header: dictionary.words.status,
        accessor: "status",
        style: { minWidth: "12rem" },
        filter: {
          active: true,
          name: "status",
          type: "select",
          options: collectionStatusList,
          multi: true,
          optionStyle: getShipmentStatusOptionStyle,
          col: 3,
        },
        Cell: ({ row }) => {
          const status = collectionStatusList.find(
            (elem) => elem.value === row.original.status
          );
          return (
            <span style={{ backgroundColor: status.color }} className="badge">
              {status.label}
            </span>
          );
        },
      },
      {
        position: 2,
        Header: dictionary.registries.carrier,
        accessor: "carrierCode",
        filter: {
          active: true,
          name: "carrierCode",
          type: "async-select",
          loadOptions: (searchValue, pagination) => {
            return getAllCarriers(searchValue, pagination, {
              orderBy: "asc",
              selector: "name",
            });
          },
          multi: true,
          optionStyle: getCarrierOptionStyle,
          col: 3,
        },
        Cell: ({ cell }) => {
          const row = cell.row.original;
          if (row.carrierCode) {
            return <img width={50} src={row.carrier.logoUrl} />;
          }
          return "-";
        },
      },
      {
        position: 3,
        Header: dictionary.dates.collection_date,
        accessor: "date",
        sortable: true,
        Cell: ({ cell }) => {
          return moment(cell.row.original.date).format("DD/MM/YYYY");
        },
      },
      {
        position: 4,
        Header: dictionary.registries.customer,
        accessor: "owner.name",
      },
      {
        position: 5,
        Header: dictionary.references.internal_reference,
        accessor: "internalReference",
      },
      {
        position: 6,
        Header: dictionary.references.external_reference,
        accessor: "externalReference",
      },
      {
        position: 7,
        Header: dictionary.references.carrier_reference,
        accessor: "carrierReference",
      },

      {
        Header: (
          <>
            <div
              className={
                "multi-action-label scale-animation " +
                getClassForMultiActionElement(statusOfTableRows).labelClass
              }
            >
              {dictionary.words.actions}
            </div>
            <UncontrolledDropdown
              className={
                getClassForMultiActionElement(statusOfTableRows).dropDownClass
              }
            >
              <DropdownToggle
                role="button"
                tag="button"
                type="button"
                href="#"
                className={"btn border-0 text-body"}
                data-bs-toggle="dropdown"
              >
                <i
                  className={
                    "ph-fill ph-list text-primary fw-bolder multi-action-icon scale-animation " +
                    getClassForMultiActionElement(statusOfTableRows).iconClass
                  }
                ></i>
              </DropdownToggle>
              <DropdownMenu className={"dropdown-menu-end "} container={"body"}>
                <DropdownItem
                  href="#"
                  className={
                    allSelectedCollectionsCanBeDeleted(
                      statusOfTableRows,
                      data.content
                    )
                      ? "text-danger"
                      : "disabled"
                  }
                  onClick={() =>
                    setModalSettings({
                      ...modalSettings,
                      title: "Elimina ritiri selezionati",
                      icon: "ph-trash",
                      open: !modalSettings.open,
                      collections: data.content.filter((collection) =>
                        statusOfTableRows
                          .filter((element) => element.value)
                          .map((x) => x.id)
                          .includes(collection.id)
                      ),
                      action: "delete",
                    })
                  }
                >
                  <i className="ph-trash me-2"></i>
                  {dictionary.actions.delete_selected}
                </DropdownItem>
              </DropdownMenu>
            </UncontrolledDropdown>
          </>
        ),
        pinning: "right",
        accessor: "id",
        Cell: ({ row }) => {
          const { original } = row;
          return (
            <UncontrolledDropdown>
              <DropdownToggle
                role="button"
                tag="button"
                type="button"
                href="#"
                className="btn border-0 text-body"
                data-bs-toggle="dropdown"
              >
                <i className="ph-list"></i>
              </DropdownToggle>
              <DropdownMenu className="dropdown-menu-end" container={"body"}>
                {original.status === "DRAFT" && (
                  <DropdownItem
                    href="#"
                    onClick={() => {
                      setModalSettings({
                        ...modalSettings,
                        title: dictionary.collections.edit_collection,
                        icon: "ph-note-pencil",
                        open: !modalSettings.open,
                        action: "edit",
                        id: original.id,
                        carrierCode: original.carrierCode,
                        addressBookId: original.addressBook?.id,
                      });
                    }}
                  >
                    <i className="ph-note-pencil me-2"></i>
                    {dictionary.actions.edit}
                  </DropdownItem>
                )}
                {original.status === "DRAFT" && (
                  <DropdownItem
                    href="#"
                    onClick={() => {
                      setModalSettings({
                        ...modalSettings,
                        title:
                          dictionary.collections
                            .do_you_want_to_book_a_collection,
                        icon: "ph-calendar-check",
                        open: !modalSettings.open,
                        id: original.id,
                        action: "book",
                      });
                    }}
                  >
                    <i className="ph-calendar-check me-2"></i>
                    {dictionary.actions.book}
                  </DropdownItem>
                )}
                <DropdownItem
                  href="#"
                  onClick={() => {
                    setModalSettings({
                      ...modalSettings,
                      title: dictionary.collections.details_collection,
                      icon: "ph-magnifying-glass",
                      open: !modalSettings.open,
                      action: "detail",
                      id: row.original.id,
                    });
                  }}
                >
                  <i className="ph-magnifying-glass me-2"></i>
                  {dictionary.actions.details}
                </DropdownItem>
                {original.status === "DRAFT" && (
                  <DropdownItem
                    className="text-danger"
                    href="#"
                    onClick={() => {
                      setModalSettings({
                        ...modalSettings,
                        title: dictionary.collections.delete_collection,
                        icon: "ph-trash",
                        open: !modalSettings.open,
                        action: "delete",
                        collections: [{ id: row.original.id }],
                      });
                    }}
                  >
                    <i className="ph-trash me-2"></i>
                    {dictionary.actions.delete}
                  </DropdownItem>
                )}
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      },
    ],
    "position"
  );

  return (
    <React.Fragment>
      {window.location.pathname === "/layout_no_header" ||
      window.location.pathname === "/layout_fixed_header" ? (
        ""
      ) : (
        <BreadcrumbItem
          title={dictionary.collections.collections}
          breadcrumbItem={dictionary.collections.collections}
        />
      )}

      <div className="content" id="container">
        <Card>
          <CardBody>
            <div className="row">
              <div className="col">
                <button
                  color="light"
                  type="button"
                  className="btn btn-outline-success btn-sm float-end"
                  onClick={() =>
                    setModalSettings({
                      ...modalSettings,
                      title: dictionary.collections.create_new_collection,
                      icon: "ph-plus",
                      action: "create",
                      open: !modalSettings.open,
                    })
                  }
                >
                  {dictionary.collections.create_new_collection}{" "}
                  <i className="ph-plus ms-2"></i>
                </button>
              </div>
            </div>
            <TableAdvanced
              selectMultipleRows={true}
              statusOfTableRows={statusOfTableRows}
              onChangeStatusRow={onChangeStatusRow}
              onChangeStatusAllRows={onChangeStatusAllRows}
              hasFilter={true}
              columns={allColumns}
              data={data}
              className="custom-header-css"
              tableClass={"table-xxs"}
              paginationOpt={pagination}
              onPaginationChange={handlePagination}
              sortingOpt={sort}
              onSortingChange={handleSorting}
              refreshTable={refreshData}
              onSearch={onSearch}
              omnisearch={{
                active: false,
                onSearch: setFreeSearch,
                placeholder: "Nome",
              }}
              showLoader={loading}
            />
          </CardBody>
        </Card>
      </div>

      <Modal
        isOpen={modalSettings.open}
        className="modal-content"
        size={modalSettings.size || "md"}
      >
        <ModalHeader toggle={modalToggle}>
          <span className="modal-title">
            <i className={modalSettings.icon + " me-2"}></i>
            {modalSettings.title}
          </span>
        </ModalHeader>
        <ModalBody>
          {modalSettings.action === "create" && (
            <CreateEdit unmountCallback={modalToggle} standAlone={true} />
          )}
          {modalSettings.action === "edit" && (
            <CreateEdit
              id={modalSettings.id}
              carrierCode={modalSettings.carrierCode}
              unmountCallback={modalToggle}
              defaultContactId={modalSettings.addressBookId}
            />
          )}
          {modalSettings.action === "detail" && (
            <Details id={modalSettings.id} />
          )}
          {modalSettings.action === "delete" && (
            <Delete
              collections={modalSettings.collections}
              unmountCallback={modalToggle}
            />
          )}
          {modalSettings.action === "book" && (
            <Book id={modalSettings.id} unmountCallback={modalToggle} />
          )}
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default Collections;
